import * as dg from 'dis-gui'
import { DARK_THEME, LIGHT_THEME } from './themes'

/**
 * Get the parent object from path, which contains the variable that needs to be changed.
 */
function getParentObj({ path, obj }) {
  let target = obj || config
  const pathSplit = path.split('.')

  let i = 0
  while (pathSplit.length - 1 > i) {
    if (!target || !target[pathSplit[i]]) {
      return target || {}
    }
    target = target[pathSplit[i]]
    i++
  }

  return target
}


/**
 * A component which gives a visual representation of variables exposed by the game, and allows the user
 * to edit these variables in realtime, and then export (download) the changes as a JSON file to share with others.
 */
export default class disGUI extends React.Component {
  componentDidMount() {
    const origs = this.props.ConfigurableVariables.Variables
    this.originalVariables = {}

    Object.keys(origs).forEach(key => this.originalVariables[key] = origs[key])
  }

  /**
   * Creates an array of JSX elements to be rendered.
   * @returns {Array} elements - Array of dis GUI elements to be rendered.
   */
  createList = (Variables, Configuration) => {
    const elements = []
    const conf = this.config || Configuration || { blacklist: [], config: {} }
    if (!conf.config) {
      conf.config = {}
    }

    // Store config to use using initial values.
    if (!this.config) {
      this.config = conf
    }

    Object.keys(Variables).forEach(key => {
      const path = key
      if (!(this.config.blacklist && this.config.blacklist.indexOf(key) > -1)) {
        elements.push(this.createItem({ obj: Variables[key], key, Configuration: this.config, path }))
      }
    })

    return elements
  }

  /**
   * Creates a single element (checkbox, dropdown, subfolder, etc.)
   */
  createItem({ obj, key, Configuration, path }) {
    const pathObj = getParentObj({ path, obj: Configuration.config }) || {}

    let conf = pathObj[key]
    if (!conf) {
      pathObj[key] = {}
      conf = pathObj[key]
    }

    const label = conf.label || key
    // TODO: Implement button support if needed.
    if (typeof obj === 'boolean') {
      return (
        <dg.Checkbox
          key={path + '.' + key}
          label={label}
          checked={obj}
          onChange={newValue => this.onPropChange(newValue, key, path) }
        />
      )
    } else if (conf.options && Array.isArray(conf.options)) {
      // Dropdown
      return (
        <dg.Select
          key={path + '.' + key}
          label={label}
          value={conf.optionLabels ? conf.optionLabels[conf.options.indexOf(obj)] : conf.options.indexOf(obj)}
          options={conf.optionLabels || conf.options}
          onChange={newValue => {
            const convertedValue = Number.isNaN(Number(newValue)) ? newValue : Number(newValue)
            const labelIndex = conf.optionLabels ? conf.optionLabels.indexOf(convertedValue) : conf.options.indexOf(convertedValue)
            this.onPropChange(conf.options[labelIndex], key, path)
          }}
        />
      )
    } else if (!Number.isNaN(Number(obj))) {
      // Number
      if (!conf.min) {
        conf.min = 0
      }
      if (!conf.max && conf.max !== 0) {
        conf.max = obj < 1 ? 1 : obj * 5
      }
      if (!conf.step) {
        conf.step = conf.max * 0.01
      }
      return (
        <dg.Number
          key={path + '.' + key}
          label={label}
          value={obj}
          min={conf.min || 0}
          max={conf.max}
          step={conf.step}
          onChange={newValue => this.onPropChange(newValue, key, path) }
        />
      )
    } else if (typeof obj === 'string') {
      // Text
      return (
        <dg.Text
          key={path + '.' + key}
          label={label}
          value={obj}
          onChange={newValue => this.onPropChange(newValue, key, path) }
        />
      )
    } else if (obj.red || obj.green || obj.blue) {
      // Color
      return (
        <dg.Color
          key={path + '.' + key}
          label={label}
          red={obj.red || 0}
          green={obj.green || 0}
          blue={obj.blue || 0}
          onChange={newValue => this.onPropChange(newValue, key, path) }
        />
      )
    } else {
      // Folder
      return (
        <dg.Folder label={label} key={key} expanded={conf.expanded}>
          { Object.keys(obj).map(k => {
            if (!(Configuration.blacklist && Configuration.blacklist.indexOf(k) > -1)) {
              return (
                this.createItem({
                  obj: obj[k],
                  key: k,
                  Configuration,
                  path: `${path}.${k}`
                })
              )
            }
          })}
        </dg.Folder>
      )
    }
  }

  /**
   * Update value in constan js file.
   */
  onPropChange(newValue, key, path) {
    const parent = getParentObj({ obj: this.props.ConfigurableVariables.Variables, path })
    parent[key] = newValue
    this.forceUpdate()
  }

  /**
   * Downloads a JSON file with the current state of the variables.
   */
  download() {
    const jsonData = JSON.stringify(this.props.ConfigurableVariables.Variables)
    const data = "text/json;charset=utf-8," + encodeURIComponent(jsonData)
    const a = document.createElement('a')
    a.href = 'data:' + data
    a.download = 'Variables.json'
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
    }, 100)
  }

  /**
   * Reverts all the variables to the original state.
   */
  revert() {
    const origs = this.props.ConfigurableVariables.Variables

    Object.keys(this.originalVariables).forEach(key => origs[key] = this.originalVariables[key])

    this.forceUpdate()
  }

  render() {
    if (!this.props.ConfigurableVariables) {
      return <div />
    }
    const { Variables = {}, Configuration = {} } = this.props.ConfigurableVariables
    const itemList = this.createList(Variables, Configuration)
    return (
      <div>
        <dg.GUI className="disGUI" style={DARK_THEME}>
          <dg.Button label="Download" onClick={() => this.download() } />
          <dg.Button label="Revert" onClick={() => this.revert() } />
          { itemList }
        </dg.GUI>
      </div>
    )
  }
}
